import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import {
  FlaskConical,
  Play,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  FileText,
  Sparkles,
  BarChart3,
  Trash2,
  X,
  Package,
  RefreshCw,
  FileInput,
  FileOutput,
  Image as ImageIcon,
  AlertCircle,
} from 'lucide-react';
import { QAGenerationProgress, SeriesDaySelector, SeriesTimeline } from '../../components/admin/qa';

type QAStatus =
  | 'pending'
  | 'extracting'
  | 'evolving'
  | 'deepening'
  | 'generating'
  | 'completed'
  | 'failed';

interface QAPackage {
  key: string;
  name: string;
  provider: string;
  tier: string;
  models: {
    writing: string;
    worldBuilding: string;
    utility: string;
    embedding: string;
  };
}

interface QALetter {
  key: string;
  name: string;
  language: string;
  type: string;
  preview: string;
  wordCount: number;
  seriesCount: number;
}

interface QARun {
  worldId: string;
  token: string;
  seriesKey: string | null;
  seriesName: string | null;
  seriesType: string | null;
  package: string | null;
  resolvedPackage: string | null;
  provider: string | null;
  createdAt: string;
  hasOutput: boolean;
  thumbnailUrl: string | null;
  letterIn: {
    id: string;
    preview: string;
    extractionStatus: string;
  } | null;
  letterOut: {
    id: string;
    subject: string;
    status: string;
    preview: string;
    wordCount: number;
  } | null;
  series: {
    total: number;
    completed: number;
  } | null;
  stateVersion: number;
}

interface RunDetail {
  world: {
    id: string;
    token: string;
    seriesKey: string | null;
    seriesName: string | null;
    seriesType: string | null;
    package: string | null;
    resolvedPackage: string | null;
    provider: string | null;
    createdAt: string;
  };
  resolvedConfig: {
    provider: string;
    generateModel: string;
    worldBuilderModel: string;
    utilityModel: string;
    embeddingModel: string;
    embeddingDimension: number;
  };
  input: {
    id: string;
    text: string;
    extraction: unknown;
    createdAt: string;
  } | null;
  output: {
    id: string;
    subject: string;
    text: string;
    plan: unknown;
    consistency: unknown;
    createdAt: string;
  } | null;
  qualityMetrics: {
    wordCount: number;
    sentenceCount: number;
    paragraphCount: number;
    avgWordsPerSentence: number;
    hasGreeting: boolean;
    hasSignoff: boolean;
    firstPerson: number;
  } | null;
  series: {
    total: number;
    completed: number;
    nextDay: number | null;
    days: Array<{
      day: number;
      letterInId: string;
      letterOutId: string | null;
      status: string;
      sentAt: string | null;
      receivedAt: string;
    }>;
  } | null;
  worldState: {
    version: number;
    canon: unknown;
    diffs: unknown[];
  } | null;
}

function StatusBadge({ status }: { status: QAStatus }) {
  const config: Record<
    QAStatus,
    {
      icon: typeof Clock;
      color: string;
      bg: string;
      label: string;
      spin?: boolean;
    }
  > = {
    pending: {
      icon: Clock,
      color: 'text-mist',
      bg: 'bg-stone/30',
      label: 'Pending',
    },
    extracting: {
      icon: Loader2,
      color: 'text-rune',
      bg: 'bg-rune/20',
      label: 'Extracting',
      spin: true,
    },
    evolving: {
      icon: Loader2,
      color: 'text-rune',
      bg: 'bg-rune/20',
      label: 'Evolving',
      spin: true,
    },
    deepening: {
      icon: Loader2,
      color: 'text-rune',
      bg: 'bg-rune/20',
      label: 'Deepening',
      spin: true,
    },
    generating: {
      icon: Sparkles,
      color: 'text-rune',
      bg: 'bg-rune/20',
      label: 'Generating',
      spin: true,
    },
    completed: {
      icon: CheckCircle,
      color: 'text-success',
      bg: 'bg-success/20',
      label: 'Completed',
    },
    failed: {
      icon: XCircle,
      color: 'text-ember',
      bg: 'bg-ember/20',
      label: 'Failed',
    },
  };

  const c = config[status];
  const Icon = c.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs ${c.color} ${c.bg}`}
    >
      <Icon size={14} className={c.spin ? 'animate-spin' : ''} />
      {c.label}
    </span>
  );
}

function QualityMeter({
  label,
  value,
  max,
  good,
}: {
  label: string;
  value: number;
  max: number;
  good: boolean;
}) {
  const percent = Math.min(100, (value / max) * 100);
  return (
    <div className="flex items-center gap-2">
      <span className="text-mist text-xs w-24">{label}</span>
      <div className="flex-1 bg-stone/30 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${good ? 'bg-success' : 'bg-ember'}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-parchment text-xs w-12 text-right">{value}</span>
    </div>
  );
}

// Compact run item for sidebar
function RunListItem({
  run,
  isActive,
  isRunning,
  onSelect,
}: {
  run: QARun;
  isActive: boolean;
  isRunning: boolean;
  onSelect: () => void;
}) {
  const statusIcon = run.hasOutput ? (
    <CheckCircle size={12} className="text-success" />
  ) : run.letterOut?.status === 'failed' ? (
    <XCircle size={12} className="text-ember" />
  ) : run.letterOut?.status === 'generating' ? (
    <Loader2 size={12} className="text-rune animate-spin" />
  ) : (
    <Clock size={12} className="text-mist" />
  );

  // Extract short name from seriesName (e.g., "English - Tilman to Arae Saren" -> "Tilman → Arae")
  const shortName = run.seriesName
    ? run.seriesName.replace(/^(English|German)\s*-\s*/, '').replace(' to ', ' → ')
    : run.token.split('-')[0];

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-2 rounded-lg border transition-all ${
        isActive
          ? 'border-rune bg-rune/10'
          : 'border-transparent hover:border-stone/30 hover:bg-stone/10'
      }`}
    >
      <div className="flex items-center gap-2">
        {run.thumbnailUrl ? (
          <img
            src={run.thumbnailUrl}
            alt=""
            className="w-8 h-8 rounded object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-8 h-8 rounded bg-stone/20 flex items-center justify-center flex-shrink-0">
            <ImageIcon size={12} className="text-mist/30" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {statusIcon}
            <span className="text-parchment text-xs font-medium truncate">{shortName}</span>
            {isActive && isRunning && (
              <span className="text-rune text-[10px] font-medium">LIVE</span>
            )}
          </div>
          <div className="text-mist/60 text-[10px] truncate">
            {run.resolvedPackage?.split(':')[1] ?? 'default'} •{' '}
            {new Date(run.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            {run.letterOut && ` • ${run.letterOut.wordCount}w`}
          </div>
        </div>
      </div>
    </button>
  );
}

export function QAPage() {
  // Config state
  const [packages, setPackages] = useState<QAPackage[]>([]);
  const [letters, setLetters] = useState<QALetter[]>([]);
  const [defaultPackage, setDefaultPackage] = useState<string>('quill-premium');
  const [configLoading, setConfigLoading] = useState(true);

  // Selection state
  const [selectedLetter, setSelectedLetter] = useState<string>('en');
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<number>(0);
  const [sendAllDays, setSendAllDays] = useState(false);
  const [sendCount, setSendCount] = useState<number>(1);

  // Run state
  const [isRunning, setIsRunning] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [runStatus, setRunStatus] = useState<QAStatus | null>(null);
  const [generationStage, setGenerationStage] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  // Lightbox state
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Results state
  const [runDetail, setRunDetail] = useState<RunDetail | null>(null);
  const [showInputLetter, setShowInputLetter] = useState(false);
  const [showExtraction, setShowExtraction] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [seriesExpanded, setSeriesExpanded] = useState(false);

  // History state
  const [runs, setRuns] = useState<QARun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);

  // Load config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const data = await adminApi.getQAConfig();
        setPackages(data.packages);
        setLetters(data.letters);
        setDefaultPackage(data.systemDefaults.package);
      } catch (err) {
        console.error('Failed to load QA config:', err);
      } finally {
        setConfigLoading(false);
      }
    };
    loadConfig();
  }, []);

  // Load runs
  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const data = await adminApi.getQARuns(20);
      setRuns(data.runs);
    } catch (err) {
      console.error('Failed to load runs:', err);
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  // Poll for status when running
  useEffect(() => {
    if (!activeRunId || runStatus === 'completed' || runStatus === 'failed') return;

    const interval = setInterval(async () => {
      try {
        const status = await adminApi.getQAStatus(activeRunId);
        setRunStatus(status.status);
        setGenerationStage(status.generationStage);

        if (status.status === 'completed' || status.status === 'failed') {
          setIsRunning(false);
          const detail = await adminApi.getQARunDetail(activeRunId);
          setRunDetail(detail);
          loadRuns();
        }
      } catch (err) {
        console.error('Failed to get status:', err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [activeRunId, runStatus, loadRuns]);

  // Refresh series progress until all days are completed
  useEffect(() => {
    if (!activeRunId || !runDetail?.series) return;
    if (runDetail.series.completed >= runDetail.series.total) return;

    const interval = setInterval(async () => {
      try {
        const detail = await adminApi.getQARunDetail(activeRunId);
        setRunDetail(detail);
        loadRuns();
      } catch (err) {
        console.error('Failed to refresh run detail:', err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [activeRunId, runDetail?.series?.completed, runDetail?.series?.total, loadRuns]);

  // Start a new run
  const handleStartRun = async () => {
    setIsRunning(true);
    setRunError(null);
    setRunDetail(null);
    setRunStatus('pending');
    setGenerationStage(null);

    try {
      const result = await adminApi.startQARun({
        letterKey: selectedLetter as 'en' | 'de' | 'en-space' | 'en-stress' | 'en-first-contact',
        packageKey: selectedPackage,
        day: selectedDay,
        sendAll: sendAllDays,
        count: sendAllDays ? sendCount : undefined,
      });
      setActiveRunId(result.worldId);
      setRunStatus('extracting');
      loadRuns();
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Failed to start run');
      setIsRunning(false);
      setRunStatus('failed');
    }
  };

  // Load run details
  const handleViewRun = async (worldId: string) => {
    try {
      const detail = await adminApi.getQARunDetail(worldId);
      setRunDetail(detail);
      setActiveRunId(worldId);
      setRunStatus(detail.output ? 'completed' : 'failed');
      setIsRunning(false);
    } catch (err) {
      console.error('Failed to load run details:', err);
    }
  };

  // Delete a run
  const handleDeleteRun = async (worldId: string) => {
    try {
      await adminApi.deleteQARun(worldId);
      if (activeRunId === worldId) {
        setActiveRunId(null);
        setRunDetail(null);
        setRunStatus(null);
      }
      loadRuns();
    } catch (err) {
      console.error('Failed to delete run:', err);
    }
  };

  // Delete all QA runs
  const handleCleanAll = async () => {
    if (!confirm('Delete ALL QA runs? This cannot be undone.')) return;
    try {
      const result = await adminApi.deleteAllQARuns();
      setActiveRunId(null);
      setRunDetail(null);
      setRunStatus(null);
      loadRuns();
      alert(`Deleted ${result.deleted} QA worlds`);
    } catch (err) {
      console.error('Failed to clean all runs:', err);
      alert('Failed to delete QA runs');
    }
  };

  const selectedLetterDetails = letters.find((letter) => letter.key === selectedLetter);
  useEffect(() => {
    if (selectedLetterDetails) {
      setSendCount(selectedLetterDetails.seriesCount);
    }
  }, [selectedLetterDetails]);

  return (
    <div className="flex gap-6 h-[calc(100vh-8rem)]">
      {/* Main Content */}
      <div className="flex-1 min-w-0 overflow-y-auto space-y-6 pr-2">
        {/* Header */}
        <div>
          <h1 className="font-display text-2xl text-parchment flex items-center gap-3">
            <FlaskConical className="text-rune" />
            QA Testing
          </h1>
          <p className="text-mist text-sm mt-1">
            Test letter generation pipelines with different configurations
          </p>
        </div>

        {/* Configuration Panel - Compact */}
        <div className="bg-night border border-stone/30 rounded-xl p-4">
          {configLoading ? (
            <div className="text-mist animate-pulse py-4 text-center">Loading...</div>
          ) : (
            <div className="space-y-4">
              {/* Top row: Letter + Package + Run */}
              <div className="flex flex-wrap items-end gap-4">
                {/* Letter Selection */}
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-mist text-xs mb-1.5">Test Series</label>
                  <select
                    value={selectedLetter}
                    onChange={(e) => {
                      setSelectedLetter(e.target.value);
                      setSelectedDay(0);
                      setSendAllDays(false);
                    }}
                    className="w-full bg-stone/20 border border-stone/30 rounded-lg px-3 py-2 text-parchment text-sm"
                  >
                    {letters.map((letter) => (
                      <option key={letter.key} value={letter.key}>
                        {letter.name} ({letter.seriesCount} days)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Package Selection */}
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-mist text-xs mb-1.5">Model Package</label>
                  <select
                    value={selectedPackage ?? ''}
                    onChange={(e) => setSelectedPackage(e.target.value || null)}
                    className="w-full bg-stone/20 border border-stone/30 rounded-lg px-3 py-2 text-parchment text-sm"
                  >
                    <option value="">{defaultPackage} (default)</option>
                    {[...new Set(packages.map((p) => p.provider))].map((provider) => (
                      <optgroup
                        key={provider}
                        label={provider.charAt(0).toUpperCase() + provider.slice(1)}
                      >
                        {packages
                          .filter((p) => p.provider === provider)
                          .map((p) => (
                            <option key={p.key} value={p.key}>
                              {p.name}
                            </option>
                          ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                {/* Run Button */}
                <button
                  onClick={handleStartRun}
                  disabled={isRunning}
                  className="px-6 py-2 bg-rune text-night rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-rune/90 transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  {isRunning ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Running
                    </>
                  ) : (
                    <>
                      <Play size={16} />
                      Run Test
                    </>
                  )}
                </button>
              </div>

              {/* Series options - only show if selected letter has multiple days */}
              {selectedLetterDetails && selectedLetterDetails.seriesCount > 1 && (
                <SeriesDaySelector
                  totalDays={selectedLetterDetails.seriesCount}
                  selectedDay={selectedDay}
                  sendAllDays={sendAllDays}
                  sendCount={sendCount}
                  onDaySelect={setSelectedDay}
                  onSendAllChange={setSendAllDays}
                  onSendCountChange={setSendCount}
                />
              )}

              {runError && (
                <div className="flex items-center gap-2 text-ember text-sm bg-ember/10 rounded-lg px-3 py-2">
                  <AlertCircle size={14} />
                  {runError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Active Run Progress - Prominent when running */}
        {isRunning && runStatus && (
          <div className="bg-rune/5 border border-rune/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Loader2 size={16} className="text-rune animate-spin" />
                <span className="text-parchment font-medium">Running Test</span>
              </div>
              <StatusBadge status={runStatus} />
            </div>
            <QAGenerationProgress
              status={runStatus}
              generationStage={
                generationStage as 'planning' | 'drafting' | 'polishing' | 'checking' | null
              }
            />
          </div>
        )}

        {/* Run Results */}
        {runDetail && (
          <div className="bg-night border border-stone/30 rounded-xl p-4 space-y-4">
            {/* Header with status and links */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="font-display text-lg text-parchment">
                  {runDetail.world.seriesName ?? 'Test Results'}
                </h2>
                {!isRunning && runStatus && <StatusBadge status={runStatus} />}
              </div>
              <div className="flex items-center gap-2">
                {runDetail.input && (
                  <Link
                    to={`/admin/letters-in/${runDetail.input.id}`}
                    className="inline-flex items-center gap-1 text-xs text-mist hover:text-rune px-2 py-1 rounded border border-stone/30 hover:border-rune/30 transition-colors"
                  >
                    <FileInput size={12} />
                    Input
                  </Link>
                )}
                {runDetail.output && (
                  <Link
                    to={`/admin/letters-out/${runDetail.output.id}`}
                    className="inline-flex items-center gap-1 text-xs text-mist hover:text-rune px-2 py-1 rounded border border-stone/30 hover:border-rune/30 transition-colors"
                  >
                    <FileOutput size={12} />
                    Output
                  </Link>
                )}
                <button
                  onClick={() => handleDeleteRun(runDetail.world.id)}
                  className="p-1.5 text-mist hover:text-ember transition-colors"
                  title="Delete run"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            {/* Config summary */}
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center gap-1.5 text-mist">
                <Package size={12} />
                <span className="text-parchment">
                  {runDetail.world.resolvedPackage ?? 'default'}
                </span>
              </div>
              <div className="text-mist">
                Model: <span className="text-parchment font-mono">{runDetail.resolvedConfig.generateModel}</span>
              </div>
              <div className="text-mist">
                State: <span className="text-parchment">v{runDetail.worldState?.version ?? 0}</span>
              </div>
            </div>

            {/* Quality Metrics - Compact */}
            {runDetail.qualityMetrics && (
              <div className="bg-void/50 rounded-lg p-3">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <QualityMeter
                      label="Words"
                      value={runDetail.qualityMetrics.wordCount}
                      max={500}
                      good={runDetail.qualityMetrics.wordCount >= 100}
                    />
                    <QualityMeter
                      label="Paragraphs"
                      value={runDetail.qualityMetrics.paragraphCount}
                      max={10}
                      good={runDetail.qualityMetrics.paragraphCount >= 3}
                    />
                  </div>
                  <div className="space-y-1">
                    <QualityMeter
                      label="Sentences"
                      value={runDetail.qualityMetrics.sentenceCount}
                      max={30}
                      good={runDetail.qualityMetrics.sentenceCount >= 5}
                    />
                    <QualityMeter
                      label="Avg W/S"
                      value={runDetail.qualityMetrics.avgWordsPerSentence}
                      max={25}
                      good={
                        runDetail.qualityMetrics.avgWordsPerSentence >= 10 &&
                        runDetail.qualityMetrics.avgWordsPerSentence <= 20
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <QualityMeter
                      label="1st Person"
                      value={runDetail.qualityMetrics.firstPerson}
                      max={20}
                      good={runDetail.qualityMetrics.firstPerson >= 3}
                    />
                    <div className="flex items-center gap-3 text-xs h-6">
                      <span
                        className={
                          runDetail.qualityMetrics.hasGreeting ? 'text-success' : 'text-ember'
                        }
                      >
                        {runDetail.qualityMetrics.hasGreeting ? '✓' : '✗'} Greeting
                      </span>
                      <span
                        className={
                          runDetail.qualityMetrics.hasSignoff ? 'text-success' : 'text-ember'
                        }
                      >
                        {runDetail.qualityMetrics.hasSignoff ? '✓' : '✗'} Sign-off
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Series Progress */}
            {runDetail.series && (
              <SeriesTimeline
                total={runDetail.series.total}
                completed={runDetail.series.completed}
                nextDay={runDetail.series.nextDay}
                days={runDetail.series.days}
                expanded={seriesExpanded}
                onToggleExpand={() => setSeriesExpanded(!seriesExpanded)}
              />
            )}

            {/* Generated Letter */}
            {runDetail.output && (
              <div>
                <h3 className="text-parchment text-sm font-medium mb-2 flex items-center gap-2">
                  <Sparkles size={14} className="text-rune" />
                  {runDetail.output.subject}
                </h3>
                <div className="bg-void/50 rounded-lg p-3 text-mist text-sm whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto">
                  {runDetail.output.text}
                </div>
              </div>
            )}

            {/* Collapsible Technical Details */}
            <div className="space-y-1 pt-2 border-t border-stone/20">
              {runDetail.input != null && (
                <button
                  onClick={() => setShowInputLetter(!showInputLetter)}
                  className="w-full flex items-center justify-between p-2 rounded text-mist hover:text-parchment hover:bg-stone/10 transition-colors text-sm"
                >
                  <span className="flex items-center gap-2">
                    <FileText size={14} />
                    Input Letter
                  </span>
                  {showInputLetter ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              )}
              {showInputLetter && runDetail.input && (
                <div className="bg-void/50 rounded-lg p-3 text-mist text-sm whitespace-pre-wrap max-h-48 overflow-y-auto mb-2">
                  {runDetail.input.text}
                </div>
              )}

              {!!runDetail.input?.extraction && (
                <button
                  onClick={() => setShowExtraction(!showExtraction)}
                  className="w-full flex items-center justify-between p-2 rounded text-mist hover:text-parchment hover:bg-stone/10 transition-colors text-sm"
                >
                  <span className="flex items-center gap-2">
                    <BarChart3 size={14} />
                    Extraction Data
                  </span>
                  {showExtraction ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              )}
              {showExtraction && runDetail.input?.extraction != null && (
                <pre className="bg-void/50 rounded-lg p-3 text-mist text-xs overflow-auto max-h-48 mb-2">
                  {String(JSON.stringify(runDetail.input.extraction, null, 2))}
                </pre>
              )}

              {!!runDetail.output?.plan && (
                <button
                  onClick={() => setShowPlan(!showPlan)}
                  className="w-full flex items-center justify-between p-2 rounded text-mist hover:text-parchment hover:bg-stone/10 transition-colors text-sm"
                >
                  <span className="flex items-center gap-2">
                    <FileText size={14} />
                    Generation Plan
                  </span>
                  {showPlan ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              )}
              {showPlan && runDetail.output?.plan != null && (
                <pre className="bg-void/50 rounded-lg p-3 text-mist text-xs overflow-auto max-h-48">
                  {String(JSON.stringify(runDetail.output.plan, null, 2))}
                </pre>
              )}
            </div>
          </div>
        )}

        {/* Empty state when no run selected */}
        {!runDetail && !isRunning && (
          <div className="bg-night border border-stone/30 rounded-xl p-8 text-center">
            <FlaskConical size={32} className="text-mist/30 mx-auto mb-3" />
            <p className="text-mist">
              Configure and run a test above, or select a previous run from the sidebar.
            </p>
          </div>
        )}
      </div>

      {/* Sidebar - Run History */}
      <div className="w-64 flex-shrink-0 bg-night border border-stone/30 rounded-xl p-3 flex flex-col h-full">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-sm text-parchment">Recent Runs</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={loadRuns}
              disabled={runsLoading}
              className="p-1 text-mist hover:text-parchment transition-colors"
              title="Refresh"
            >
              <RefreshCw size={12} className={runsLoading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={handleCleanAll}
              disabled={runs.length === 0}
              className="p-1 text-mist hover:text-ember disabled:opacity-30 transition-colors"
              title="Delete all runs"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1">
          {runs.length === 0 ? (
            <div className="text-mist/50 text-xs text-center py-4">No runs yet</div>
          ) : (
            runs.map((run) => (
              <RunListItem
                key={run.worldId}
                run={run}
                isActive={activeRunId === run.worldId}
                isRunning={isRunning && activeRunId === run.worldId}
                onSelect={() => handleViewRun(run.worldId)}
              />
            ))
          )}
        </div>
      </div>

      {/* Thumbnail Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-void/90"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 text-mist hover:text-parchment transition-colors"
            onClick={() => setLightboxUrl(null)}
          >
            <X size={24} />
          </button>
          <img
            src={lightboxUrl}
            alt="World thumbnail"
            className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
